// Activity: what each person on the team did, newest first.
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { teamApi } from '../../services/account';
import { Button, Badge, Notice, formatWhen } from './ui';

const ACTIONS = [
  { value: '', label: 'All activity' },
  { value: 'auth', label: 'Sign-ins & passwords' },
  { value: 'brand', label: 'Brands added' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'email', label: 'Emails sent' },
  { value: 'mailbox', label: 'Mailboxes' },
  { value: 'contacts', label: 'Contact lookups' },
  { value: 'member', label: 'Team changes' },
  { value: 'template', label: 'Templates' },
];

const ACTION_LABEL = {
  'auth.login': ['Signed in', 'gray'],
  'auth.password_set': ['Chose password', 'gray'],
  'brand.add': ['Added brand', 'blue'],
  'pipeline.add': ['Pipeline', 'green'],
  'pipeline.stage': ['Pipeline', 'green'],
  'pipeline.delete': ['Pipeline', 'amber'],
  'email.send': ['Email sent', 'purple'],
  'mailbox.connect': ['Mailbox', 'purple'],
  'mailbox.remove': ['Mailbox', 'amber'],
  'contacts.search': ['Contacts', 'blue'],
  'tracker.add': ['Position tracker', 'blue'],
  'member.invite': ['Team', 'purple'],
  'member.update': ['Team', 'purple'],
  'member.remove': ['Team', 'red'],
  'member.resend_invite': ['Team', 'purple'],
  'member.permissions': ['Authority', 'purple'],
  'member.permissions_bulk': ['Authority', 'purple'],
  'template.create': ['Template', 'gray'],
  'template.update': ['Template', 'gray'],
  'template.delete': ['Template', 'gray'],
};

const PAGE_SIZE = 50;

const ActivityTab = ({ data, memberFilter, setMemberFilter }) => {
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const people = [
    ...(data?.owner ? [{ id: data.owner.id, name: `${data.owner.name} (owner)` }] : []),
    ...((data?.members || []).map((m) => ({ id: m.id, name: m.name || m.email }))),
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const next = await teamApi.activity({
        member: memberFilter || undefined,
        action: action || undefined,
        from: from || undefined,
        to: to ? new Date(new Date(to).getTime() + 86400000).toISOString().slice(0, 10) : undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setResult(next);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [memberFilter, action, from, to, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [memberFilter, action, from, to]);

  const pages = Math.max(1, Math.ceil((result.total || 0) / PAGE_SIZE));
  const selectClass = 'rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm text-gray-800 dark:text-slate-100';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <select className={selectClass} value={memberFilter || ''} onChange={(e) => setMemberFilter(e.target.value ? Number(e.target.value) : null)}>
          <option value="">Everyone</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className={selectClass} value={action} onChange={(e) => setAction(e.target.value)}>
          {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <label className="text-xs text-gray-500 dark:text-slate-400">From<input type="date" className={`${selectClass} ml-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-xs text-gray-500 dark:text-slate-400">To<input type="date" className={`${selectClass} ml-1`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <Button variant="secondary" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>
      {error && <Notice>{error}</Notice>}

      <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
        {result.items.map((a) => {
          const [label, tone] = ACTION_LABEL[a.action] || [a.action, 'gray'];
          return (
            <div key={a.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
              <div className="w-28 shrink-0 text-xs text-gray-500 dark:text-slate-400" title={new Date(a.created_at).toLocaleString()}>{formatWhen(a.created_at)}</div>
              <div className="w-40 shrink-0 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{a.actor_name}</div>
                {a.impersonator_id && <div className="text-[11px] text-amber-600">via MARS support</div>}
              </div>
              <Badge tone={tone}>{label}</Badge>
              <div className="flex-1 min-w-[12rem] text-sm text-gray-700 dark:text-slate-300 break-words">{a.summary}</div>
            </div>
          );
        })}
        {!result.items.length && !loading && (
          <p className="px-4 py-10 text-center text-sm text-gray-500 dark:text-slate-400">No activity for these filters yet.</p>
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-slate-300">
          <span>{result.total} entries</span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Newer</Button>
            <span>Page {page + 1} of {pages}</span>
            <Button variant="secondary" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Older</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
